import { Dtos, SuretyAccountId } from "@inrev/common";
import { useUpdateAccountHistory } from "@portal/api/account";
import { FormCurrencyComponent, FormYesNoComponent } from "@portal/components/form/fieldLayouts";
import { Modal } from "@portal/components/layout/Modal";
import { ModalItemWithHeader } from "@portal/components/layout/ModalItemWithHeader";
import { createFormResolver, normalizeFormValues } from "@portal/components/multiform";
import { Button, Card, PartialIndicator } from "@portal/components/primitives";
import { FormSchemaProvider } from "@portal/providers/FormSchemaProvider";
import { FC, useCallback, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { match } from "ts-pattern";
import z from "zod";
import {
	getNestedValue,
	getPartialFieldPaths,
	getRequiredForApprovalFieldPaths,
	hasOwnPath,
	isEmpty,
} from "../../../../../utils/schema-fields";
import { commercialOnlyHistorySchema } from "../../historySchemaForAccountDetail";

type HistoryEditModalProps = {
	accountId: SuretyAccountId;
	modelSize: NonNullable<Dtos.SuretyAccount.Get.Response["modelSize"]>;
	history: Dtos.SuretyAccount.Metadata.Get.Response;
	updatedAt: Dtos.SuretyAccount.Get.Response["updatedAt"];
	showContractHistory: boolean;
	onClose: () => void;
};

const metadataBaseSchema = z.object({
	largestProjectCompleted: z.coerce.number().nullish().meta({ partial: true }),
	failedToCompleteAProject: z.boolean().nullish().meta({ partial: true }),
	lostAPaymentSuit: z.boolean().nullish().meta({ partial: true }),
	causedSuretyLoss: z.boolean().nullish().meta({ partial: true }),
	constructionProjectsGrossLossGt10PercentLast36Months: z
		.boolean()
		.nullish()
		.meta({ partial: true }),
	currentConstructionProjectExpectedGrossLoss: z.boolean().nullish().meta({ partial: true }),
});

type HistoryFormData = Dtos.SuretyAccount.Update.History.Request;

const historyFormDefaultValues = (
	history: HistoryEditModalProps["history"],
	showContractHistory: boolean,
) => {
	const commercialOnly = {
		causedSuretyLoss: history.causedSuretyLoss ?? null,
	};
	if (!showContractHistory) return commercialOnly;

	return {
		...commercialOnly,
		largestProjectCompleted: history.contract?.largestProjectCompleted ?? null,
		failedToCompleteAProject: history.contract?.failedToCompleteAProject ?? null,
		lostAPaymentSuit: history.contract?.lostAPaymentSuit ?? null,
		constructionProjectsGrossLossGt10PercentLast36Months:
			history.contract?.constructionProjectsGrossLossGt10PercentLast36Months ?? null,
		currentConstructionProjectExpectedGrossLoss:
			history.contract?.currentConstructionProjectExpectedGrossLoss ?? null,
	};
};

export const HistoryEditModal: FC<HistoryEditModalProps> = ({
	accountId,
	modelSize,
	history,
	updatedAt,
	showContractHistory,
	onClose,
}) => {
	const { updateAccountHistory, updateAccountHistoryIsLoading } = useUpdateAccountHistory(
		accountId,
		{ onConflict: onClose },
	);
	const formSchema = useMemo(() => {
		if (!showContractHistory) return commercialOnlyHistorySchema;

		return match(modelSize)
			.with("small", () =>
				metadataBaseSchema.extend({
					constructionProjectsGrossLossGt10PercentLast36Months: z.boolean().nullish(),
					currentConstructionProjectExpectedGrossLoss: z.boolean().nullish(),
				}),
			)
			.with("medium", () => metadataBaseSchema)
			.with("large", () => metadataBaseSchema)
			.exhaustive();
	}, [modelSize, showContractHistory]);

	const formMethods = useForm<HistoryFormData>({
		resolver: createFormResolver(formSchema),
		defaultValues: historyFormDefaultValues(history, showContractHistory),
		mode: "onBlur",
	});

	const { handleSubmit, formState } = formMethods;

	const [partialModalOpen, setPartialModalOpen] = useState(false);
	const pendingDataRef = useRef<HistoryFormData | null>(null);

	const submitData = useCallback(
		(data: HistoryFormData) => {
			const payload = showContractHistory
				? { ...data, updatedAt }
				: { causedSuretyLoss: data.causedSuretyLoss, updatedAt };
			updateAccountHistory(payload, { onSuccess: onClose });
		},
		[onClose, showContractHistory, updateAccountHistory, updatedAt],
	);

	const onValidSubmit = useCallback(
		(data: HistoryFormData) => {
			const normalized = normalizeFormValues(data, {
				returnEmptyObject: true,
				returnEmptyArray: true,
			}) as HistoryFormData;

			const partialPaths = getPartialFieldPaths(formSchema, normalized);
			const reqForApprovalPaths = getRequiredForApprovalFieldPaths(formSchema, normalized);
			const allPaths = [...partialPaths, ...reqForApprovalPaths];
			const emptyPaths = allPaths.filter(
				(path) => hasOwnPath(normalized, path) && isEmpty(getNestedValue(normalized, path)),
			);

			if (emptyPaths.length > 0) {
				pendingDataRef.current = normalized;
				setPartialModalOpen(true);
				return;
			}

			submitData(normalized);
		},
		[formSchema, submitData],
	);

	const confirmPartialSubmit = useCallback(() => {
		setPartialModalOpen(false);
		if (pendingDataRef.current) {
			submitData(pendingDataRef.current);
			pendingDataRef.current = null;
		}
	}, [submitData]);

	return (
		<>
			<Modal onClickOutside={onClose} itemClassName="flex w-full justify-center px-4">
				<ModalItemWithHeader
					header="Edit History"
					onClose={onClose}
					className="flex max-h-[75vh] w-full max-w-[min(720px,92vw)] flex-col overflow-hidden"
					bodyClassName="flex min-h-0 flex-1 flex-col"
				>
					<FormSchemaProvider value={formSchema}>
						<form onSubmit={handleSubmit(onValidSubmit)} className="flex min-h-0 flex-1 flex-col">
							<div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-10 pt-6 pb-8">
								<div className="flex flex-col space-y-10">
									{showContractHistory && (
										<>
											<FormCurrencyComponent
												formMethods={formMethods}
												name="largestProjectCompleted"
												label="What is the value of the largest project completed by the companies in this account?"
												inputClassName="max-w-[300px]"
												partialRequiresSubmit={false}
												testId="largest-project-completed"
											/>
											<FormYesNoComponent
												formMethods={formMethods}
												name="failedToCompleteAProject"
												label="Has any company in this account ever failed to complete a project?"
												deselectable
												partialRequiresSubmit={false}
												testId="failed-to-complete-a-project"
											/>
											<FormYesNoComponent
												formMethods={formMethods}
												name="lostAPaymentSuit"
												label="Has any company in this account ever lost a payment suit?"
												deselectable
												partialRequiresSubmit={false}
												testId="lost-a-payment-suit"
											/>
										</>
									)}
									<FormYesNoComponent
										formMethods={formMethods}
										name="causedSuretyLoss"
										label="Has any company in this account ever caused a loss to a surety?"
										deselectable
										partialRequiresSubmit={false}
										testId="caused-surety-loss"
									/>
									{showContractHistory && (
										<>
											<FormYesNoComponent
												formMethods={formMethods}
												name="constructionProjectsGrossLossGt10PercentLast36Months"
												label="In the past 3 years, has any company in this account experienced a gross loss on any project greater than 10% of that project's contract value?"
												tooltip="For example, a gross loss of at least $10,000 on a $100,000 project"
												deselectable
												partialRequiresSubmit={false}
												testId="gross-loss-gt-10-percent"
											/>
											<FormYesNoComponent
												formMethods={formMethods}
												name="currentConstructionProjectExpectedGrossLoss"
												label="Does any company in this account expect a gross loss on a project they are currently working on?"
												deselectable
												partialRequiresSubmit={false}
												testId="current-project-expected-gross-loss"
											/>
										</>
									)}
								</div>
							</div>
							<div className="flex shrink-0 justify-end gap-3 border-t border-bc-default px-10 py-4">
								<Button
									type="button"
									variant="outline"
									color="secondary"
									onClick={onClose}
									disabled={updateAccountHistoryIsLoading}
								>
									Cancel
								</Button>
								<Button
									type="submit"
									color="primary"
									loading={updateAccountHistoryIsLoading}
									disabled={!formState.isDirty}
								>
									Save
								</Button>
							</div>
						</form>
					</FormSchemaProvider>
				</ModalItemWithHeader>
			</Modal>

			{partialModalOpen && (
				<Modal onClickOutside={() => setPartialModalOpen(false)}>
					<Card className="flex flex-col items-center w-[550px] max-w-[90vw] gap-[10px] overflow-hidden">
						<div className="w-full flex items-center space-x-[12px] justify-between bg-intent-warning-bg px-[18px] py-[10px]">
							<span className="text-[16px] font-[550]">Ineligible for Auto-Approval</span>
						</div>
						<div className="flex flex-1 px-5 py-4 text-[16px] text-primary gap-[10px]">
							<span className="mt-2 px-3">
								<PartialIndicator active />
							</span>
							<div className="flex flex-col flex-1 gap-3 text-[18px]">
								<span>
									Some information is missing, so this submission will be referred to an underwriter
									for review.
								</span>
								<span className="text-[16px] text-secondary">
									After submitting, you can provide additional information on the Account page.
								</span>
							</div>
						</div>
						<div className="w-full flex justify-center gap-[10px] px-4 py-3 mb-[10px]">
							<Button
								color="primary"
								className="order-2 px-[20px] min-w-[120px]"
								onClick={confirmPartialSubmit}
								loading={updateAccountHistoryIsLoading}
							>
								Submit
							</Button>
							<Button
								color="secondary"
								variant="outline"
								className="order-1 w-[120px]"
								onClick={() => setPartialModalOpen(false)}
							>
								Back
							</Button>
						</div>
					</Card>
				</Modal>
			)}
		</>
	);
};
